/**
 * ExerciseTestReviewPage - BCM Manager page to review and approve exercise tests
 * ISO 22301 Clause 8.5 - Exercising and Testing
 * TurboTax-style StepDialog for review workflow
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import StepDialog from "../components/StepDialog";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Activity,
  Target,
  Timer,
  Award,
  ClipboardCheck,
} from "lucide-react";

const RESULT_STATUS = {
  passed: { label: "Passed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  failed: { label: "Failed", color: "bg-red-100 text-red-700", icon: XCircle },
  partial: { label: "Partial", color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  pending: { label: "Pending", color: "bg-slate-100 text-slate-700", icon: Clock },
};

export default function ExerciseTestReviewPage() {
  const { api } = useAuth();

  const [loading, setLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [complianceStats, setComplianceStats] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // StepDialog state
  const [showReviewWizard, setShowReviewWizard] = useState(false);
  const [reviewStep, setReviewStep] = useState(0);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewAction, setReviewAction] = useState("");
  const [reviewFeedback, setReviewFeedback] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [reviewsResponse, complianceResponse] = await Promise.all([
        api.get("/recovery-packages/pending-exercise-reviews"),
        api.get("/recovery-packages/exercise-compliance-contribution")
      ]);
      setPendingReviews(reviewsResponse.data);
      setComplianceStats(complianceResponse.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openReviewWizard = (review) => {
    setSelectedReview(review);
    setReviewAction("");
    setReviewFeedback("");
    setReviewStep(0);
    setShowReviewWizard(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedReview || !reviewAction) return;

    if ((reviewAction === "request_retest" || reviewAction === "reject") && !reviewFeedback.trim()) {
      toast.error("Please provide feedback for retest or rejection");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/recovery-packages/${selectedReview.package_id}/exercises/${selectedReview.test_id}/review`, {
        action: reviewAction,
        feedback: reviewFeedback || null,
      });

      const actionLabel = {
        approve: "approved",
        request_retest: "sent back for re-testing",
        reject: "rejected"
      };

      toast.success(`Exercise test ${actionLabel[reviewAction]}`);
      setShowReviewWizard(false);
      setSelectedReview(null);
      fetchData();
    } catch (error) {
      console.error("Failed to submit review:", error);
      toast.error("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  const result = selectedReview ? (RESULT_STATUS[selectedReview?.overall_result] || RESULT_STATUS.pending) : null;

  return (
    <div className="space-y-6" data-testid="exercise-test-review-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-amber-100 text-amber-700">CHECK Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 8.5</Badge>
            <Badge className="bg-sky-100 text-sky-700">BCM Manager</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Exercise Test Review</h1>
          <p className="text-slate-600 mt-1">
            Review and approve exercise tests submitted by process owners
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Compliance Stats */}
      {complianceStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-500">Total Packages</p>
              </div>
              <p className="text-2xl font-bold text-slate-900" data-testid="stat-total-packages">{complianceStats.total_packages}</p>
            </CardContent>
          </Card>
          <Card className={complianceStats.testing_coverage_pct < 80 ? "border-amber-200 bg-amber-50" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-500">Testing Coverage</p>
              </div>
              <p className="text-2xl font-bold text-sky-700" data-testid="stat-coverage">{complianceStats.testing_coverage_pct}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <p className="text-sm text-slate-500">Approved Tests</p>
              </div>
              <p className="text-2xl font-bold text-emerald-600" data-testid="stat-approved">{complianceStats.approved_tests}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Timer className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-500">RTO Success Rate</p>
              </div>
              <p className="text-2xl font-bold text-blue-600" data-testid="stat-rto">{complianceStats.rto_success_rate}%</p>
            </CardContent>
          </Card>
          <Card className={complianceStats.status === "compliant" ? "border-emerald-200 bg-emerald-50" : complianceStats.status === "partial" ? "border-amber-200 bg-amber-50" : "border-red-200 bg-red-50"}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Award className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-500">Clause 8.5 Score</p>
              </div>
              <p className={`text-2xl font-bold ${
                complianceStats.status === "compliant" ? "text-emerald-600" : 
                complianceStats.status === "partial" ? "text-amber-600" : "text-red-600"
              }`} data-testid="stat-clause-score">
                {complianceStats.compliance_contribution_score}%
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pending Reviews Count */}
      <Card className={pendingReviews.length > 0 ? "border-blue-200 bg-blue-50" : ""}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Pending Reviews</p>
                <p className="text-sm text-slate-500">Exercise tests awaiting your approval</p>
              </div>
            </div>
            <Badge className="text-lg px-4 py-1 bg-blue-100 text-blue-700" data-testid="pending-count">
              {pendingReviews.length}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Pending Reviews List */}
      {pendingReviews.length === 0 ? (
        <Card>
          <CardContent className="pt-12 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300 mb-4" />
            <p className="text-slate-500" data-testid="empty-state">No pending exercise tests to review</p>
            <p className="text-sm text-slate-400 mt-1">
              Process owners will submit their exercise tests here for your approval
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pendingReviews.map((review) => {
            const res = RESULT_STATUS[review.overall_result] || RESULT_STATUS.pending;
            const ResultIcon = res.icon;
            const rtoMet = review.rto_achieved && review.rto_target && review.rto_achieved <= review.rto_target;
            const passedSteps = review.procedure_results?.filter(r => r.status === "passed").length || 0;
            const totalSteps = review.procedure_results?.length || 0;

            return (
              <Card key={`${review.package_id}-${review.test_id}`} className="border" data-testid={`review-card-${review.test_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className={`p-3 rounded-lg ${res.color}`}>
                        <ResultIcon className="w-6 h-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-lg">{review.package_name}</h3>
                        <p className="text-slate-500">{review.department}</p>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm">
                            <span className="text-slate-500">Exercise:</span>{" "}
                            <span className="font-medium">{review.exercise_name}</span>
                            <span className="text-slate-400 ml-2 capitalize">({review.exercise_type?.replace("_", " ")})</span>
                          </p>
                          <p className="text-sm">
                            <span className="text-slate-500">Submitted by:</span>{" "}
                            <span className="font-medium">{review.tested_by}</span>
                            <span className="text-slate-400 ml-2">
                              on {new Date(review.submitted_at).toLocaleDateString()}
                            </span>
                          </p>
                          {totalSteps > 0 && (
                            <p className="text-sm">
                              <span className="text-slate-500">Procedure Steps:</span>{" "}
                              <span className={passedSteps === totalSteps ? "text-emerald-600" : "text-amber-600"}>
                                {passedSteps}/{totalSteps} passed
                              </span>
                            </p>
                          )}
                        </div>

                        {/* RTO Achievement */}
                        <div className="mt-3 flex items-center gap-4">
                          {review.rto_target && (
                            <div className="flex items-center gap-2 text-sm">
                              <Target className="w-4 h-4 text-slate-400" />
                              <span>Target RTO: {review.rto_target}h</span>
                            </div>
                          )}
                          {review.rto_achieved && (
                            <div className={`flex items-center gap-2 text-sm ${rtoMet ? "text-emerald-600" : "text-red-600"}`}>
                              <Timer className="w-4 h-4" />
                              <span>Achieved: {review.rto_achieved}h</span>
                              {rtoMet ? (
                                <CheckCircle2 className="w-4 h-4" />
                              ) : (
                                <XCircle className="w-4 h-4" />
                              )}
                            </div>
                          )}
                        </div>

                        {review.submission_notes && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <p className="text-sm text-slate-600">{review.submission_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <Button
                      onClick={() => openReviewWizard(review)}
                      className="bg-sky-700 hover:bg-sky-800 ml-4 shrink-0"
                      data-testid={`review-btn-${review.test_id}`}
                    >
                      <ClipboardCheck className="w-4 h-4 mr-2" />
                      Review
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── StepDialog Wizard for Exercise Review ── */}
      <StepDialog
        open={showReviewWizard}
        onOpenChange={(open) => { setShowReviewWizard(open); if (!open) { setReviewStep(0); setSelectedReview(null); } }}
        title="Review Exercise Test"
        currentStep={reviewStep}
        onStepChange={setReviewStep}
        onSubmit={handleSubmitReview}
        onCancel={() => setShowReviewWizard(false)}
        submitLabel={submitting ? "Submitting..." : (
          reviewAction === "approve" ? "Approve Test" :
          reviewAction === "request_retest" ? "Request Retest" :
          reviewAction === "reject" ? "Reject Test" : "Submit"
        )}
        submitDisabled={submitting || !reviewAction || ((reviewAction === "request_retest" || reviewAction === "reject") && !reviewFeedback.trim())}
        saving={submitting}
        maxWidth="max-w-2xl"
        canAdvance={
          reviewStep === 0 ? true :
          reviewStep === 1 ? !!reviewAction :
          true
        }
        steps={[
          { id: "summary", title: "Test Summary", subtitle: "Review the exercise test details and results submitted by the process owner.", isComplete: true },
          { id: "decision", title: "Decision", subtitle: "What is your decision for this exercise test?", isComplete: !!reviewAction },
          { id: "feedback", title: "Feedback & Confirm", subtitle: "Provide your feedback and confirm your review decision.", isComplete: false },
        ]}
      >
        {/* Step 0: Test Summary */}
        {reviewStep === 0 && selectedReview && (
          <div className="space-y-4" data-testid="step-summary">
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Package</span>
                <span className="font-medium">{selectedReview.package_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Department</span>
                <span className="font-medium">{selectedReview.department}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Exercise</span>
                <span className="font-medium">{selectedReview.exercise_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Type</span>
                <span className="font-medium capitalize">{selectedReview.exercise_type?.replace("_", " ")}</span>
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-slate-500">Result</span>
                <Badge className={result?.color}>{result?.label}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Submitted By</span>
                <span className="font-medium">{selectedReview.tested_by}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Date</span>
                <span className="font-medium">{new Date(selectedReview.submitted_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* RTO Section */}
            {(selectedReview.rto_target || selectedReview.rto_achieved) && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-blue-800 mb-2">RTO Achievement</p>
                <div className="flex items-center gap-6">
                  {selectedReview.rto_target && (
                    <div className="text-sm">
                      <span className="text-blue-600">Target:</span> <span className="font-medium">{selectedReview.rto_target}h</span>
                    </div>
                  )}
                  {selectedReview.rto_achieved && (
                    <div className="text-sm">
                      <span className="text-blue-600">Achieved:</span> <span className="font-medium">{selectedReview.rto_achieved}h</span>
                    </div>
                  )}
                  {selectedReview.rto_achieved && selectedReview.rto_target && (
                    <Badge className={selectedReview.rto_achieved <= selectedReview.rto_target ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {selectedReview.rto_achieved <= selectedReview.rto_target ? "RTO Met" : "RTO Exceeded"}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Procedure Steps */}
            {selectedReview.procedure_results?.length > 0 && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-semibold text-slate-700 mb-2">Procedure Steps</p>
                <div className="space-y-1">
                  {selectedReview.procedure_results.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      {step.status === "passed" ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-slate-600">Step {idx + 1}: {step.name || `Procedure ${idx + 1}`}</span>
                      <Badge variant="outline" className="text-xs ml-auto capitalize">{step.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedReview.submission_notes && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs font-semibold text-amber-700 mb-1">Submission Notes</p>
                <p className="text-sm text-amber-800">{selectedReview.submission_notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Decision */}
        {reviewStep === 1 && (
          <div className="space-y-3" data-testid="step-decision">
            <Label className="text-base font-semibold">Your Decision</Label>
            {[
              {
                value: "approve",
                label: "Approve",
                desc: "This exercise test meets ISO 22301 Clause 8.5 requirements. It will contribute to compliance scoring.",
                icon: ThumbsUp,
                color: "border-emerald-400 bg-emerald-50",
                iconColor: "text-emerald-600"
              },
              {
                value: "request_retest",
                label: "Request Re-test",
                desc: "The exercise needs improvement. The process owner will need to re-conduct the test.",
                icon: RotateCcw,
                color: "border-amber-400 bg-amber-50",
                iconColor: "text-amber-600"
              },
              {
                value: "reject",
                label: "Reject",
                desc: "This exercise test does not meet requirements. The process owner will need to create a new test.",
                icon: ThumbsDown,
                color: "border-red-400 bg-red-50",
                iconColor: "text-red-600"
              },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setReviewAction(opt.value)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  reviewAction === opt.value
                    ? `${opt.color} ring-1 ring-opacity-50`
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`action-${opt.value}`}
              >
                <div className="flex items-center gap-3">
                  <opt.icon className={`w-5 h-5 ${reviewAction === opt.value ? opt.iconColor : 'text-slate-400'}`} />
                  <div>
                    <p className="font-medium text-sm">{opt.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Feedback & Confirm */}
        {reviewStep === 2 && (
          <div className="space-y-4" data-testid="step-feedback">
            {/* Review summary */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Package</span>
                <span className="font-medium">{selectedReview?.package_name}</span>
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-slate-500">Decision</span>
                <Badge className={
                  reviewAction === "approve" ? 'bg-emerald-100 text-emerald-700' :
                  reviewAction === "request_retest" ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }>
                  {reviewAction === "approve" ? "Approve" : reviewAction === "request_retest" ? "Request Retest" : "Reject"}
                </Badge>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Feedback {reviewAction !== "approve" && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                value={reviewFeedback}
                onChange={(e) => setReviewFeedback(e.target.value)}
                placeholder={
                  reviewAction === "approve"
                    ? "Optional: Add any comments for the process owner..."
                    : "Required: Explain why re-test or rejection is needed..."
                }
                rows={4}
                data-testid="review-feedback-input"
              />
              {(reviewAction === "request_retest" || reviewAction === "reject") && !reviewFeedback.trim() && (
                <p className="text-xs text-red-500">Feedback is required for retest or rejection decisions.</p>
              )}
            </div>
          </div>
        )}
      </StepDialog>
    </div>
  );
}
